import React, { useState } from 'react';
import { 
  Camera,
  Plus,
  Trash2,
  Edit3,
  ToggleLeft,
  ToggleRight,
  Target,
  CheckCircle,
  FileText,
  Shield,
  Info,
  Upload
} from 'lucide-react';

const DocumentsSettings = ({ 
  settings,
  handleSettingChange,
  currentCompany
}) => {
  
  // Document management state
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [newDocument, setNewDocument] = useState({
    name: '',
    category: 'Identity',
    description: '',
    required: false
  });

  // Available categories with metadata
  const documentCategories = [
    { value: 'License', icon: '🪪', color: 'blue', description: 'Driving licenses and permits' },
    { value: 'Identity', icon: '👤', color: 'green', description: 'ID cards and identity documents' },
    { value: 'Travel', icon: '✈️', color: 'purple', description: 'Passports, visas, and travel documents' },
    { value: 'Payment', icon: '💳', color: 'yellow', description: 'Credit cards and payment methods' },
    { value: 'Insurance', icon: '🛡️', color: 'red', description: 'Insurance certificates and coverage' },
    { value: 'Verification', icon: '✅', color: 'cyan', description: 'Proof of address and verification' },
    { value: 'Other', icon: '📄', color: 'gray', description: 'Other document types' }
  ];

  // Safe document access - always returns array
  const getDocumentsSafely = () => {
    return Array.isArray(settings.documentRequirements) ? settings.documentRequirements : [];
  };

  // Safe filter for enabled documents
  const getEnabledDocuments = () => {
    const documents = getDocumentsSafely();
    return documents.filter(document => document && document.enabled === true);
  };

  // Safe filter for required documents
  const getRequiredDocuments = () => {
    const documents = getEnabledDocuments();
    return documents.filter(document => document.required === true);
  };

  // Group documents by category
  const getDocumentsByCategory = () => {
    const documents = getDocumentsSafely();
    const grouped = {};
    
    documents.forEach(doc => {
      if (!grouped[doc.category]) {
        grouped[doc.category] = [];
      }
      grouped[doc.category].push(doc);
    });
    
    return grouped;
  };

  // Document management functions
  const addDocument = () => {
    if (!newDocument.name.trim()) {
      alert('Please enter document name');
      return;
    }

    const currentDocuments = getDocumentsSafely();
    const newId = currentDocuments.length > 0 ? Math.max(...currentDocuments.map(d => d.id || 0)) + 1 : 1;
    
    const document = {
      id: newId,
      name: newDocument.name.trim(),
      category: newDocument.category,
      enabled: false,
      required: newDocument.required,
      description: newDocument.description.trim()
    };

    handleSettingChange('documentRequirements', [...currentDocuments, document]);

    setNewDocument({ name: '', category: 'Identity', description: '', required: false });
    setShowAddDocument(false);
  };

  const removeDocument = (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document requirement?')) {
      return;
    }
    
    const currentDocuments = getDocumentsSafely();
    const updatedDocuments = currentDocuments.filter(d => d && d.id !== documentId);
    handleSettingChange('documentRequirements', updatedDocuments);
  };

  const editDocument = (document) => {
    setEditingDocument(document);
    setNewDocument({
      name: document.name,
      category: document.category,
      description: document.description || '',
      required: document.required
    });
    setShowAddDocument(true);
  };

  const updateDocument = () => {
    if (!newDocument.name.trim()) {
      alert('Please enter document name');
      return;
    }

    const currentDocuments = getDocumentsSafely();
    const updatedDocuments = currentDocuments.map(d => 
      d && d.id === editingDocument.id 
        ? {
            ...d,
            name: newDocument.name.trim(),
            category: newDocument.category,
            description: newDocument.description.trim(),
            required: newDocument.required
          }
        : d
    );

    handleSettingChange('documentRequirements', updatedDocuments);

    setEditingDocument(null);
    setNewDocument({ name: '', category: 'Identity', description: '', required: false });
    setShowAddDocument(false);
  };

  const toggleDocument = (documentId) => {
    const currentDocuments = getDocumentsSafely();
    const updatedDocuments = currentDocuments.map(d => 
      d && d.id === documentId ? { ...d, enabled: !d.enabled } : d
    );
    handleSettingChange('documentRequirements', updatedDocuments);
  };

  const updateDocumentProperty = (documentId, property, value) => {
    const currentDocuments = getDocumentsSafely();
    const updatedDocuments = currentDocuments.map(d => 
      d && d.id === documentId ? { ...d, [property]: value } : d
    );
    handleSettingChange('documentRequirements', updatedDocuments);
  };

  const cancelDocumentForm = () => {
    setShowAddDocument(false);
    setEditingDocument(null);
    setNewDocument({ name: '', category: 'Identity', description: '', required: false });
  };

  const getCategoryColor = (category) => {
    const cat = documentCategories.find(c => c.value === category);
    return cat ? cat.color : 'gray';
  };

  const getCategoryIcon = (category) => {
    const cat = documentCategories.find(c => c.value === category);
    return cat ? cat.icon : '📄';
  };

  const getCategoryBgClass = (category, enabled) => {
    const colorMap = {
      License: enabled ? 'bg-blue-500/20 border-blue-500' : 'bg-blue-500/10 border-blue-700',
      Identity: enabled ? 'bg-green-500/20 border-green-500' : 'bg-green-500/10 border-green-700',
      Travel: enabled ? 'bg-purple-500/20 border-purple-500' : 'bg-purple-500/10 border-purple-700',
      Payment: enabled ? 'bg-yellow-500/20 border-yellow-500' : 'bg-yellow-500/10 border-yellow-700',
      Insurance: enabled ? 'bg-red-500/20 border-red-500' : 'bg-red-500/10 border-red-700',
      Verification: enabled ? 'bg-cyan-500/20 border-cyan-500' : 'bg-cyan-500/10 border-cyan-700',
      Other: enabled ? 'bg-gray-500/20 border-gray-500' : 'bg-gray-500/10 border-gray-700'
    };
    return colorMap[category] || (enabled ? 'bg-gray-500/20 border-gray-500' : 'bg-gray-500/10 border-gray-700');
  };

  const getCategoryTextClass = (category) => {
    const colorMap = {
      License: 'text-blue-400',
      Identity: 'text-green-400',
      Travel: 'text-purple-400',
      Payment: 'text-yellow-400',
      Insurance: 'text-red-400',
      Verification: 'text-cyan-400',
      Other: 'text-gray-400'
    };
    return colorMap[category] || 'text-gray-400';
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <div className="p-6 border-b border-gray-700 bg-gradient-to-r from-orange-900/30 to-red-900/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white flex items-center gap-3">
              <Camera className="w-6 h-6 text-orange-500" />
              Document Requirements
              <div className="ml-auto flex items-center text-orange-400 text-xs">
                <Target size={12} className="mr-1" />
                <span>Images & Documents</span>
              </div>
            </h3>
            <p className="text-gray-400 text-sm mt-1">
              Configure required documents and images for customer verification
            </p>
          </div>
          <button
            onClick={() => setShowAddDocument(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-3 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors"
          >
            <Plus size={16} />
            <span>Add Document</span>
          </button>
        </div>
      </div>
      
      <div className="p-6 space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-3 flex items-start gap-2">
          <Info size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="text-blue-300 font-medium mb-1">Document Verification System</p>
            <p className="text-blue-200">
              Enable document requirements to collect customer verification documents. Mark documents as required to make them mandatory for reservation completion. Customers can upload photos or capture documents directly using their camera.
            </p>
          </div>
        </div>

        {/* Add/Edit Document Form */}
        {showAddDocument && (
          <div className="bg-gray-700/30 rounded-lg p-4 border border-orange-500/50">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-medium flex items-center gap-2">
                <Plus size={16} className="text-orange-400" />
                {editingDocument ? 'Edit Document Requirement' : 'Add New Document Requirement'}
              </h4>
              <button
                onClick={cancelDocumentForm}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Document Name *
                </label>
                <input
                  type="text"
                  value={newDocument.name}
                  onChange={(e) => setNewDocument(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="e.g., International Driving Permit"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={newDocument.category}
                  onChange={(e) => setNewDocument(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  {documentCategories.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.value}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {documentCategories.find(c => c.value === newDocument.category)?.description}
                </p>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={newDocument.description}
                  onChange={(e) => setNewDocument(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 h-20 resize-none"
                  placeholder="Describe what this document is used for and when it's needed..."
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newDocument.required}
                    onChange={(e) => setNewDocument(prev => ({ ...prev, required: e.target.checked }))}
                    className="mr-3 text-red-500"
                  />
                  <span className="text-white">Make this document required</span>
                </label>
                <p className="text-xs text-gray-500 ml-6 mt-1">
                  Required documents must be uploaded before booking can be completed
                </p>
              </div>
            </div>

            {/* Preview */}
            {newDocument.name && (
              <div className="mt-4 bg-gray-800/50 rounded-lg p-3 border border-gray-600">
                <p className="text-xs text-gray-400 mb-2">Preview:</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{newDocument.name}</p>
                    <p className="text-xs text-gray-400">{newDocument.category}</p>
                  </div>
                  {newDocument.required && (
                    <span className="text-xs bg-red-900/30 text-red-400 px-2 py-1 rounded-full border border-red-700">
                      Required
                    </span>
                  )}
                </div>
                {newDocument.description && (
                  <p className="text-xs text-gray-400 mt-2 border-t border-gray-600 pt-2">
                    {newDocument.description}
                  </p>
                )}
              </div>
            )}
            
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={editingDocument ? updateDocument : addDocument}
                className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors"
              >
                <CheckCircle size={16} />
                {editingDocument ? 'Update' : 'Add'} Document
              </button>
              <button
                onClick={cancelDocumentForm}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Documents List */}
        <div>
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-400" />
            Configured Documents ({getDocumentsSafely().length} total, {getEnabledDocuments().length} enabled)
          </h4>
          
          {getDocumentsSafely().length === 0 ? (
            <div className="bg-gray-700/30 rounded-lg p-8 border border-gray-600 text-center">
              <Upload size={48} className="mx-auto text-gray-500 mb-4" />
              <p className="text-gray-400 mb-2">No document requirements configured yet</p>
              <p className="text-gray-500 text-sm">Click "Add Document" to create your first requirement</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getDocumentsSafely().map((document) => (
                <div 
                  key={document.id} 
                  className={`rounded-lg p-4 border transition-all ${
                    document.enabled 
                      ? 'shadow-lg shadow-orange-500/10 border-orange-500/50' 
                      : 'bg-gray-700/30 border-gray-600'
                  } ${document.enabled ? getCategoryBgClass(document.category, true) : getCategoryBgClass(document.category, false)}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    {/* ✅ CHANGED: category icon box removed - name block only */}
                    <div className="min-w-0">
                      <h5 className="text-white font-medium flex items-center gap-2">
                        {document.name}
                        {document.enabled && (
                          <span className="text-xs bg-green-900/30 text-green-400 px-2 py-0.5 rounded-full border border-green-700">
                            Active
                          </span>
                        )}
                      </h5>
                      <p className="text-gray-400 text-xs">{document.category}</p>
                      {document.required && (
                        <span className="text-xs bg-red-900/30 text-red-400 px-2 py-1 rounded-full border border-red-700 mt-1 inline-block">
                          Required
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => editDocument(document)}
                        className="p-1 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded hover:from-orange-600 hover:to-red-700 transition-colors"
                        title="Edit"
                      >
                        <Edit3 size={12} />
                      </button>
                      <button
                        onClick={() => removeDocument(document.id)}
                        className="p-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                      <button
                        onClick={() => toggleDocument(document.id)}
                        className={`transition-colors ${
                          document.enabled 
                            ? 'text-orange-400' 
                            : 'text-gray-500'
                        }`}
                        title={document.enabled ? 'Disable' : 'Enable'}
                      >
                        {document.enabled ? (
                          <ToggleRight size={24} className="text-orange-500" />
                        ) : (
                          <ToggleLeft size={24} className="text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Document Description */}
                  {document.description && (
                    <div className="text-xs text-gray-400 mb-3 bg-gray-800/50 rounded p-2 border border-gray-600">
                      {document.description}
                    </div>
                  )}

                  {/* Document Configuration - Only shown when enabled */}
                  {document.enabled && (
                    <div className="space-y-3 pt-3 border-t border-gray-600">
                      <div>
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={document.required}
                            onChange={(e) => updateDocumentProperty(document.id, 'required', e.target.checked)}
                            className="mr-2 text-red-500"
                          />
                          <span className="text-white">Required Document</span>
                        </label>
                        <p className="text-xs text-gray-500 ml-5 mt-1">
                          Customers must upload this document to complete booking
                        </p>
                      </div>

                      <div className="bg-gray-800/50 rounded p-2 border border-gray-600">
                        <div className="text-xs text-gray-400">Status:</div>
                        <div className="text-white text-sm font-medium">
                          {document.enabled ? 'Active' : 'Disabled'} • {document.required ? 'Required' : 'Optional'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary by Category */}
        {getDocumentsSafely().length > 0 && (
          <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <Shield size={16} className="text-orange-400" />
              Documents by Category
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(getDocumentsByCategory()).map(([category, documents]) => (
                <div key={category} className="bg-gray-800/50 rounded p-3 border border-gray-600">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{getCategoryIcon(category)}</span>
                    <span className="text-sm font-medium text-gray-300">{category}</span>
                  </div>
                  <div className="text-xs text-gray-400">
                    {documents.length} total • {documents.filter(d => d.enabled).length} enabled
                  </div>
                  <div className="text-xs text-gray-500">
                    {documents.filter(d => d.required).length} required
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Camera Capture Setting */}
        <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <Camera size={16} className="text-orange-400" />
            Camera Capture Settings
          </h4>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.allowCameraCapture}
              onChange={(e) => handleSettingChange('allowCameraCapture', e.target.checked)}
              className="mr-3 text-orange-500"
            />
            <div>
              <span className="text-white">Allow camera capture for documents</span>
              <p className="text-xs text-gray-500 mt-1">
                Enable customers to take photos directly using their device camera when uploading documents
              </p>
            </div>
          </label>
        </div>

        {/* Configuration Summary */}
        <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg p-4">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <CheckCircle size={16} className="text-orange-400" />
            Document Requirements Summary
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-gray-400">Total Documents:</span>
              <span className="text-white ml-2 font-medium">
                {getDocumentsSafely().length}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Enabled:</span>
              <span className={`ml-2 font-medium ${
                getEnabledDocuments().length > 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {getEnabledDocuments().length}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Required:</span>
              <span className={`ml-2 font-medium ${
                getRequiredDocuments().length > 0 ? 'text-orange-400' : 'text-gray-400'
              }`}>
                {getRequiredDocuments().length}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Camera:</span>
              <span className={`ml-2 font-medium ${
                settings.allowCameraCapture ? 'text-green-400' : 'text-red-400'
              }`}>
                {settings.allowCameraCapture ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>

        {/* Help Text */}
        <div className="text-xs text-gray-500 bg-gray-700/30 rounded p-3 border border-gray-600">
          <p className="font-medium text-gray-400 mb-1">💡 Tips:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Enable documents to make them available during reservation creation</li>
            <li>Mark important documents as "Required" to enforce collection</li>
            <li>Use clear descriptions to help customers understand what's needed</li>
            <li>Camera capture makes it easier for customers to submit documents on mobile</li>
            <li>Disabled documents remain in your system but are hidden from customers</li>
            <li>Group related documents by category for easier management</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DocumentsSettings;